import { Table, Select, Row, Col, DatePicker, Input } from "antd";
import { useContext, useEffect, useState } from "react";
import { CloudDownloadOutlined } from "@ant-design/icons";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

import moment from "moment";

import "./styles.css";

import { Context } from "../../context";
import { Fetch } from "../../fetch";
import {
  convertIdToKey,
  convertSubscriptions,
  handleExportClick,
} from "../../utils";

const { Column } = Table;
const { RangePicker } = DatePicker;

const dateFormat = "YYYY-MM-DD HH:mm:ss";
const displayDateFormat = "YYYY-MM-DD";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const ManageSubscribers = () => {
  const { token, apiUrl } = useContext(Context);

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    date: [dayjs().startOf("month"), dayjs().endOf("month")],
    optIn: null,
    searchText: null,
  });

  useEffect(() => {
    let url, start_date, end_date, search_string, date_string;
    if (filters.date[0] & filters.date[1]) {
      start_date = dayjs(filters.date[0]).format(displayDateFormat);
      end_date = dayjs(filters.date[1]).format(displayDateFormat);
      date_string = `start_date=${start_date}&end_date=${end_date}`;
    } else {
      date_string = "";
    }
    if (filters.searchText) {
      search_string = `search_text=${filters.searchText}`;
    } else {
      search_string = "";
    }

    url = `${apiUrl}/subscribers?${date_string}&${search_string}&current=${pagination.current}&size=${pagination.pageSize}`;

    Fetch(url, token, (result) => {
      const users = convertSubscriptions(convertIdToKey(result.users));
      users.forEach((user) => {
        if (!user.email) {
          user.email = "-";
        }
        if (!user.utm_source) {
          user.utm_source = "NA";
        }
        if (!user.utm_medium) {
          user.utm_medium = "NA";
        }
        if (!user.referrer) {
          user.referrer = "NA";
        }
      });
      setUsers(users);
      setPagination((p) => ({
        ...pagination,
        total: result.total,
      }));
    });
  }, [apiUrl, token, filters, pagination.current]);

  const handleTableDataChange = (paginationParam) => {
    let url;
    const startDate = moment(filters.date[0]).isValid()
      ? moment(filters.date[0]).format(dateFormat)
      : null;
    const endDate = moment(filters.date[1]).isValid()
      ? moment(filters.date[1]).format(dateFormat)
      : null;
    if (startDate && endDate && filters.optIn) {
      url = `${apiUrl}/subscribers?start_date=${startDate}&end_date=${endDate}&opt_in=${filters.optIn}&current=${paginationParam.current}&size=${paginationParam.pageSize}`;
    } else if (filters.optIn) {
      url = `${apiUrl}/subscribers?opt_in=${filters.optIn}&current=${paginationParam.current}&size=${paginationParam.pageSize}`;
    } else if (startDate && endDate) {
      url = `${apiUrl}/subscribers?start_date=${startDate}&end_date=${endDate}&current=${paginationParam.current}&size=${paginationParam.pageSize}`;
    } else {
      url = `${apiUrl}/subscribers?current=${paginationParam.current}&size=${paginationParam.pageSize}`;
    }

    Fetch(url, token, (result) => {
      const users = convertSubscriptions(convertIdToKey(result.users));
      users.forEach((user) => {
        if (!user.email) {
          user.email = "-";
        }
      });
      setUsers(users);
      setPagination({ ...paginationParam, total: result.total });
    });
  };

  const rangePresets = [
    {
      label: "Yesterday",
      value: [dayjs().add(-1, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 3 Days",
      value: [dayjs().add(-3, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 7 Days",
      value: [dayjs().add(-7, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 30 Days",
      value: [dayjs().add(-30, "d"), dayjs().add(-1, "d")],
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  return (
    <div className="manage-episodes-wrapper">
      <Row>
        <Col span={6}>
          <Input
            placeholder="Search for email"
            onChange={(e) => {
              console.log(e.target.value);
              setFilters({ ...filters, searchText: e.target.value });
            }}
            style={{ marginLeft: "1%" }}
          />
        </Col>
        <Col span={1}></Col>
        <Col span={6}>
          <RangePicker
            defaultValue={[filters.date[0], filters.date[1]]}
            onChange={(_, datestring) => {
              setFilters({
                ...filters,
                date: [dayjs(datestring[0]), dayjs(datestring[1])],
              });
            }}
            presets={rangePresets}
            allowClear={true}
          />
        </Col>
      </Row>
      <Table
        dataSource={users}
        pagination={pagination}
        onChange={handleTableChange}
      >
        <Column title="Email" dataIndex="email" key="email" align="center" />
        <Column
          title="Registered On"
          dataIndex="createdOn"
          key="createdOn"
          align="center"
          render={(_, record) => moment(record.createdOn).format(dateFormat)}
        />
        <Column
          title="Last Logged In"
          dataIndex={"updatedOn"}
          key={"updatedOn"}
          align="center"
          render={(_, record) => moment(record.updatedOn).format(dateFormat)}
        />
        <Column
          title="Source/Medium"
          align="center"
          render={(_, record) =>
            record.utm_source &&
            record.utm_medium &&
            record.utm_source !== "NA" &&
            record.utm_medium !== "NA"
              ? `${record.utm_source}/${record.utm_medium}`
              : "NA"
          }
        />
        <Column title="Referrer" align="center" dataIndex={"referrer"} />
      </Table>
      <CloudDownloadOutlined
        className="export-btn-only"
        onClick={() => handleExportClick(apiUrl, token, "users")}
      />
    </div>
  );
};

export default ManageSubscribers;
